import React, { useState, useEffect } from 'react';
import {
  Typography,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import { createApiInstance } from '../../services/api/axios-config';
import API_CONFIG from '../../config/apiConfig';

// Create API instance for conversation service (where topics endpoint lives)
const api = createApiInstance(API_CONFIG.CONVERSATION_SERVICE_URL);

// TypeScript interfaces
export interface Topic {
  id: string;
  name: string;
  category: string;
  conversationCount: number;
  messageCount: number;
  avgConfidence: number;
  lastMentioned: string;
  firstMentioned: string;
  frequencyScore: number;
  recencyBoost: number;
  rankScore: number;
}

export interface TopicMetadata {
  limit: number;
  days: number;
  total: number;
}

export interface TopicNavigationProps {
  selectedTopics: string[];
  onTopicSelect: (topicId: string) => void;
  onTopicDeselect: (topicId: string) => void;
  onClearAll: () => void;
}

const TopicNavigation: React.FC<TopicNavigationProps> = ({
  selectedTopics,
  onTopicSelect,
  onTopicDeselect,
  onClearAll,
}) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [metadata, setMetadata] = useState<TopicMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  // Fetch recent topics on mount
  useEffect(() => {
    fetchRecentTopics();
  }, []);

  const fetchRecentTopics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/api/v1/topics/recent', {
        params: {
          limit: 20,
          days: 30,
        },
      });

      setTopics(response.data.topics);
      setMetadata(response.data.metadata);
    } catch (err: any) {
      console.error('Error fetching recent topics:', err);
      setError(err.response?.data?.error || 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topicId: string) => {
    if (selectedTopics.includes(topicId)) {
      onTopicDeselect(topicId);
    } else {
      onTopicSelect(topicId);
    }
  };

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Get color for topic based on category
  const getTopicColor = (category: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    switch (category?.toLowerCase()) {
      case 'technical':
      case 'technology':
        return 'primary';
      case 'business':
        return 'secondary';
      case 'personal':
        return 'success';
      case 'project':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Format the last mentioned time
  const formatLastMentioned = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div
      style={{
        borderTop: '1px solid rgba(0,0,0,0.12)',
        paddingTop: 16,
        paddingBottom: 16,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingLeft: 16,
          paddingRight: 16,
          marginBottom: 8,
          cursor: 'pointer',
        }}
        onClick={toggleExpanded}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <LabelIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight="600">
            Recent Topics
          </Typography>
          {selectedTopics.length > 0 && (
            <Chip
              label={selectedTopics.length}
              size="small"
              color="primary"
              style={{ height: 20, minWidth: 20 }}
            />
          )}
        </div>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </div>

      {/* Topics List */}
      <Collapse in={expanded}>
        <div style={{ paddingLeft: 16, paddingRight: 16 }}>
          {/* Loading State */}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 24, paddingBottom: 24 }}>
              <CircularProgress size={24} />
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && topics.length === 0 && (
            <Typography variant="body2" color="text.secondary" style={{ paddingTop: 16, paddingBottom: 16, textAlign: 'center' }}>
              No topics found. Topics will appear as you have conversations.
            </Typography>
          )}

          {/* Topics Grid */}
          {!loading && !error && topics.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                {topics.map((topic, index) => (
                  <Tooltip
                    key={`${topic.id}_${index}`}
                    title={`${topic.messageCount} messages in ${topic.conversationCount} conversations • Last: ${formatLastMentioned(topic.lastMentioned)}`}
                    arrow
                  >
                    <Chip
                      label={topic.name}
                      size="small"
                      color={selectedTopics.includes(topic.id) ? getTopicColor(topic.category) : 'default'}
                      variant={selectedTopics.includes(topic.id) ? 'filled' : 'outlined'}
                      onClick={() => handleTopicClick(topic.id)}
                      style={{
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    />
                  </Tooltip>
                ))}
              </div>

              {/* Clear Filters Button */}
              {selectedTopics.length > 0 && (
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <Chip
                    label="Clear Filters"
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={onClearAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Metadata */}
              {metadata && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={{ display: 'block', marginTop: 12, textAlign: 'center' }}
                >
                  Showing {topics.length} {topics.length === 1 ? 'topic' : 'topics'} from last {metadata.days} days
                </Typography>
              )}
            </>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default TopicNavigation;
